import { FormEvent, ReactNode, useEffect, useRef, useState } from "react"
import { useToggle } from "react-use"
import { Link, useParams } from "react-router-dom"
import { AxiosError } from "axios"

import { ActionButtons, ErrorPage, SuccessLoginPage, VerificationCode } from "../components"
import {
    getEmailErrorMessage,
    getNameErrorMessage,
    isEmpty,
    isNameValid,
    VERIFY_CODE_PLACEHOLDER,
    VERIFY_CODE_LENGTH,
    VERIFICATION_ERROR,
    VERIFICATION_ERROR_MAP,
} from "../common"
import { CLIENT_AUTHORIZATION_PLACE } from "../Default/common/constant"

import { Header } from "./components"
import style from "./style.module.scss"
import { COMPANIES, PAGES } from "./common/constants"
import { fetchRegisterToB2b, fetchVerifyByPhoneToB2b } from "./common/api"
import { FetchRegisterToB2b } from "./common/interface"
import { CompanyRegisterStorage } from "./common/companyAuthStorage"

import { Spinner } from "components/Spinner"
import { format } from "utils/datetime-format-utils"
import { useCountdown } from "utils/hooks"
import { IS_PRODUCTION } from "utils/env-management"
import { PhoneInput } from "components"
import { focusInput, validateEmail as isEmailValid } from "utils/helpers"
import { Input } from "@components-branded/FormControls/Input/Input"
import { Button } from "@components-branded/FormControls/Button"
import { Modal } from "@components-branded/Modal"
import { Text } from "@components-branded/Text"

const handleAxiosError = (e: unknown, id?: COMPANIES) => {
    const errorText = "Неизвестная ошибка"
    if (e instanceof AxiosError && e.response) {
        const { error } = e.response.data as { error: string }
        if (
            id &&
            id === COMPANIES.DODO &&
            (error as VERIFICATION_ERROR) === VERIFICATION_ERROR.PHONE_IS_NOT_ALLOWED
        ) {
            return (
                <>
                    Ваш номер не&nbsp;прошёл верификацию. Если вы&nbsp;сотрудник Dodo Brands,
                    напишите на&nbsp;
                    <a href="mailto:s.ryabinkov@dodobrands.io">s.ryabinkov@dodobrands.io</a> для
                    доступа к&nbsp;скидке
                </>
            )
        }
        if (error in VERIFICATION_ERROR_MAP) {
            return VERIFICATION_ERROR_MAP[error as VERIFICATION_ERROR]
        }
        return errorText
    }
    return errorText
}

export const Content = () => {
    const { id } = useParams<{ id: COMPANIES }>()
    const [telephone, setTelephone] = useState(CompanyRegisterStorage.getTelephone())
    const [email, setEmail] = useState(CompanyRegisterStorage.getEmail())
    const [name, setName] = useState(CompanyRegisterStorage.getName())
    const [response, setResponse] = useState<FetchRegisterToB2b | null>(
        CompanyRegisterStorage.getResponse()
    )
    const [telephoneError, toggleTelephoneError] = useToggle(false)
    const [emailError, toggleEmailError] = useToggle(false)
    const [nameError, toggleNameError] = useToggle(false)
    const [emailValidationError, toggleEmailValidationError] = useState(false)
    const [nameValidationError, toggleNameValidationError] = useState(false)
    const [showErrors, toggleShowErrors] = useToggle(false)
    const [error, setError] = useState<ReactNode>()
    const [page, setPage] = useState(PAGES.FORM)
    const [code, setCode] = useState<string>(
        Array(VERIFY_CODE_LENGTH).fill(VERIFY_CODE_PLACEHOLDER).join("")
    )
    const [isLoading, toggleIsLoading] = useToggle(false)
    const [verificationCode, setVerificationCode] = useState<string>()
    const [resendTimeout, setResendTimeout] = useState(CompanyRegisterStorage.getResendTimeout())
    const countdown = useCountdown(resendTimeout)
    const isInitialRender = useRef(true)

    const telephoneRef = useRef<HTMLInputElement>(null)
    const emailRef = useRef<HTMLInputElement>(null)
    const nameRef = useRef<HTMLInputElement>(null)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (
            telephoneError ||
            emailError ||
            nameError ||
            emailValidationError ||
            nameValidationError
        ) {
            toggleShowErrors(true)

            if (telephoneError) {
                focusInput(telephoneRef)
            } else if (emailError || emailValidationError) {
                focusInput(emailRef)
            } else if (nameError || nameValidationError) {
                focusInput(nameRef)
            }

            return
        }

        if (!response) {
            await sendRegister(true)
        } else {
            setPage(PAGES.CODE)
        }
    }

    const sendRegister = async (toError?: boolean) => {
        if (!id) {
            return
        }

        try {
            toggleIsLoading()
            const data = await fetchRegisterToB2b({
                company: id,
                phone: telephone.replace(/\s+/g, ""),
                email,
                name,
            })

            const resendTimeout = new Date().getTime() + data.resend_timeout * 1000

            CompanyRegisterStorage.saveAllData({
                telephone,
                email,
                name,
                response: data,
                timestamp: new Date().getTime(),
                resendTimeout: resendTimeout,
            })
            setResponse(data)
            setPage(PAGES.CODE)
            setResendTimeout(new Date(resendTimeout))
            setError(undefined)

            if (!IS_PRODUCTION) {
                setVerificationCode(data.verification_code)
            }
        } catch (e) {
            setError(handleAxiosError(e, id))
            if (toError) {
                setPage(PAGES.ERROR)
            }
        } finally {
            toggleIsLoading()
        }
    }

    const handleClickCode = async () => {
        if (!response) {
            return
        }

        try {
            toggleIsLoading()
            const data = await fetchVerifyByPhoneToB2b({
                code,
                token: response.verification_token,
                phone: telephone.replace(/\s+/g, ""),
            })
            setPage(PAGES.SUCCESS)
            if (!IS_PRODUCTION) {
                const link = document.createElement("a")
                if (data.link) {
                    link.innerText = data.link
                    link.setAttribute("style", "font-size: 20px;color: red;")
                    link.setAttribute("href", data.link)
                    document.body.prepend(link)
                }
            }
        } catch (e) {
            setError(handleAxiosError(e))
        } finally {
            toggleIsLoading()
        }
    }

    const validateName = () => {
        toggleNameError(isEmpty(name))
        toggleNameValidationError(isNameValid(name))
    }

    const validateEmail = () => {
        toggleEmailError(isEmpty(email))
        toggleEmailValidationError(!isEmailValid(email))
    }

    const changeCode = (code: string) => {
        setCode(code)
    }

    const closeModal = () => {
        setPage(PAGES.FORM)
        setError(undefined)
    }

    useEffect(() => {
        validateEmail()
    }, [email])

    useEffect(() => {
        validateName()
    }, [name])

    useEffect(() => {
        if (!isInitialRender.current || !response) {
            isInitialRender.current = false
            return
        }
        setPage(PAGES.CODE)
    }, [response])

    useEffect(() => {
        const digitLength = [...code].filter((char) => /\d/.test(char)).length

        if (digitLength === VERIFY_CODE_LENGTH) {
            void handleClickCode()
        }
    }, [code])

    return (
        <>
            <Header
                onGoBack={
                    page === PAGES.ERROR
                        ? () => {
                              setPage(PAGES.FORM)
                          }
                        : undefined
                }
            />
            <div className="redesign-container">
                <div className={style.content}>
                    {page === PAGES.FORM && (
                        <div className={style.card}>
                            <form onSubmit={(data) => void handleSubmit(data)}>
                                <Text className={style.headline} isAccent>
                                    Создайте личный кабинет
                                </Text>
                                <div className={style.text}>
                                    В&nbsp;нем будут все ваши сессии, контакты психолога
                                    и&nbsp;полезные материалы.
                                </div>
                                <PhoneInput
                                    ref={telephoneRef}
                                    label="Телефон"
                                    name="telephone"
                                    required
                                    defaultValue={telephone}
                                    onChangeValue={setTelephone}
                                    showErrors={showErrors}
                                    errorCallback={toggleTelephoneError}
                                    autoFocus={telephone.length > 0}
                                />
                                <Input
                                    ref={emailRef}
                                    label="Ваша почта"
                                    name="email"
                                    value={email}
                                    error={getEmailErrorMessage(
                                        showErrors,
                                        emailError,
                                        emailValidationError
                                    )}
                                    onChange={setEmail}
                                />
                                <Input
                                    ref={nameRef}
                                    isMiddleSpacesAllowed
                                    label="Имя"
                                    name="name"
                                    value={name}
                                    error={getNameErrorMessage(
                                        showErrors,
                                        nameError,
                                        nameValidationError
                                    )}
                                    onChange={setName}
                                />
                                <ActionButtons
                                    disabled={isLoading}
                                    showPersonalInfo={true}
                                    actionButtonLabel="Зарегистрироваться"
                                />
                            </form>
                        </div>
                    )}
                    {page === PAGES.CODE && (
                        <Modal onBackgroundClick={closeModal} isActive>
                            <Modal.Content className={style.modal} isFullHeightMobile padding="S">
                                {verificationCode && (
                                    <div
                                        className={style.modalVerificationCode}
                                        onClick={() => setCode(verificationCode)}
                                    >
                                        {verificationCode}
                                    </div>
                                )}
                                {isLoading && <Spinner />}
                                {!isLoading && (
                                    <>
                                        <div className={style.modalContainer}>
                                            <div className={style.modalContent}>
                                                <div className={style.headline}>
                                                    Введите код из&nbsp;сообщения
                                                </div>
                                                <VerificationCode
                                                    code={code}
                                                    error={!!error}
                                                    onChangeCode={changeCode}
                                                />
                                                {error && (
                                                    <Text className={style.error}>{error}</Text>
                                                )}
                                            </div>
                                        </div>
                                        <Text className={style.modalVerificationAgain}>
                                            {resendTimeout >= new Date() ? (
                                                <>
                                                    Прислать еще раз через{" "}
                                                    {format(new Date(countdown), "mm:ss")}
                                                </>
                                            ) : (
                                                <button
                                                    className={style.modalVerificationResend}
                                                    onClick={() => void sendRegister()}
                                                >
                                                    Прислать еще раз
                                                </button>
                                            )}
                                        </Text>
                                    </>
                                )}
                            </Modal.Content>
                        </Modal>
                    )}
                    {page === PAGES.SUCCESS && (
                        <SuccessLoginPage
                            email={email}
                            place={CLIENT_AUTHORIZATION_PLACE.MAIN}
                            className={style.isCard}
                        />
                    )}
                    {page === PAGES.ERROR && (
                        <ErrorPage
                            headline="Ошибка : ("
                            secondary={error}
                            place={CLIENT_AUTHORIZATION_PLACE.MAIN}
                            className={style.isCard}
                        >
                            {error === VERIFICATION_ERROR.PHONE_EXISTS ? (
                                <Link
                                    to="/my/authorization"
                                    className="button-primary is-full mgv-3"
                                >
                                    Войти
                                </Link>
                            ) : (
                                <Button
                                    isFullwidth
                                    color="primary"
                                    className="mgv-3"
                                    onClick={() => setPage(PAGES.FORM)}
                                >
                                    Зарегистрироваться
                                </Button>
                            )}
                        </ErrorPage>
                    )}
                </div>
            </div>
        </>
    )
}
